import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  POST_HEARING_QUEUE_CODE,
  PostHearingTaskPage,
  PostHearingCompletionSource,
  WorkflowDecisionRecord,
  WorkflowDecisionRecordPage,
} from "./post-hearing-task.model";

const WORKLIST_URL = "/api/platform/workflows/worklist";
const RUNTIME_BASE = "/api/platform/workflows";
const DEFAULT_PAGE_SIZE = "100";
const DECISION_WORKLIST_URL = "/api/platform/workflows/worklist/decisions";
const GRANT_LEGAL_REVIEW_PURPOSE = "grant_legal_review";
const POST_HEARING_COMPLETION_APPROVAL_PURPOSE = "post_hearing_completion_approval";

const quietHttpOptions = {
  headers: new HttpHeaders({
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
  }),
  withCredentials: true,
};

/**
 * Post-hearing task service (story 967). Reuses the existing workflow worklist and runtime
 * endpoints for reads and assignment. Completion uses the explicit PHMS application command
 * with the viewed source and idempotency intent; it does not directly edit a runtime state.
 */
@Injectable({ providedIn: "root" })
export class PostHearingTaskService {
  private readonly http = inject(HttpClient);

  getTasks(statusCode?: string): Observable<PostHearingTaskPage> {
    let params = new HttpParams()
      .set("pageSize", DEFAULT_PAGE_SIZE)
      .set("queueCode", POST_HEARING_QUEUE_CODE);
    const normalizedStatus = statusCode?.trim();
    if (normalizedStatus) {
      params = params.set("statusCode", normalizedStatus);
    }

    return this.http.get<PostHearingTaskPage>(WORKLIST_URL, { ...quietHttpOptions, params });
  }

  getPendingLegalReviews(): Observable<WorkflowDecisionRecordPage> {
    const params = new HttpParams()
      .set("pageSize", DEFAULT_PAGE_SIZE)
      .set("decisionPurposeCode", GRANT_LEGAL_REVIEW_PURPOSE)
      .set("statusCode", "pending");
    return this.http.get<WorkflowDecisionRecordPage>(DECISION_WORKLIST_URL, { ...quietHttpOptions, params });
  }

  getPendingPostHearingApprovals(): Observable<WorkflowDecisionRecordPage> {
    const params = new HttpParams()
      .set("pageSize", DEFAULT_PAGE_SIZE)
      .set("decisionPurposeCode", POST_HEARING_COMPLETION_APPROVAL_PURPOSE)
      .set("statusCode", "pending");
    return this.http.get<WorkflowDecisionRecordPage>(DECISION_WORKLIST_URL, { ...quietHttpOptions, params });
  }

  approvePostHearingCompletion(decisionRecordId: number, expectedETag?: string): Observable<WorkflowDecisionRecord> {
    return this.http.post<WorkflowDecisionRecord>(
      `/api/phms/workflows/decisions/${encodeURIComponent(String(decisionRecordId))}/post-hearing/approve`,
      { reasonCode: "post_hearing_review_complete" },
      this.mutationOptions(expectedETag),
    );
  }

  claim(workflowInstanceId: number, expectedETag?: string): Observable<unknown> {
    return this.http.post(
      `${RUNTIME_BASE}/${encodeURIComponent(String(workflowInstanceId))}/assignment/claim`,
      null,
      this.mutationOptions(expectedETag),
    );
  }

  completeStep(workflowInstanceId: number, stepCode: string, expectedETag?: string, reasonCode?: string): Observable<unknown> {
    return this.http.post(
      `/api/phms/workflows/${encodeURIComponent(String(workflowInstanceId))}/post-hearing/complete`,
      { stepCode, reasonCode: reasonCode ?? null },
      this.mutationOptions(expectedETag),
    );
  }

  getCompletionSource(workflowInstanceId: number, stepCode: string): Observable<PostHearingCompletionSource> {
    return this.http.get<PostHearingCompletionSource>(`${RUNTIME_BASE}/${encodeURIComponent(String(workflowInstanceId))}/concurrency`, {
      ...quietHttpOptions, params: new HttpParams().set("stepCode", stepCode),
    });
  }

  private mutationOptions(expectedETag?: string) {
    return {
      headers: new HttpHeaders({
        "skip-error-interceptor": "yes",
        "skip-loading-interceptor": "yes",
        "Idempotency-Key": newIdempotencyKey(),
        ...(expectedETag ? { "If-Match": expectedETag } : {}),
      }),
      withCredentials: true,
    };
  }
}

function newIdempotencyKey(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return cryptoApi?.randomUUID ? cryptoApi.randomUUID() : `phms-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
